import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
// styles
import styles from "./ReceivingTable.module.scss";
// components
import Table from "../../common/Table";

// packages
import { useTranslation } from "react-i18next";

// types
import {
  ESenderRole,
  IContractDetailsResponseData,
  UserProfile,
} from "../../../setup/api/services/contract/models";
import useLocalStorage from "../../../hooks/useLocalStorage/useLocalStorage";
import { IUser } from "../../../setup/api/services/auth/models";
import { MilestonePartyStatus } from "../partyStatus";
import ContractActions from "../../../pages/contract/actions";
import { ContractContext } from "../../../providers/contract";
import { useParams } from "react-router-dom";
import { ECONTRACT_STATUS } from "../../../enums";
interface ReceivingTableProps {
  partiesObj: Partial<UserProfile>;
  milestone: IContractDetailsResponseData;
  canMakeContractActions: (
    userInfo: Partial<UserProfile> | undefined
  ) => boolean | undefined;
}

export type RowData = {
  id: string;
  name: string;
  number: string;
  amount: string;
  fee: string;
  receive: string;
};

const ReceivingTable: React.FC<ReceivingTableProps> = ({
  partiesObj,
  milestone,
  canMakeContractActions,
}) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [rows, setRows] = useState<RowData[]>([]);
  const [userInfo] = useLocalStorage<IUser | null>("userInfo", null);
  const { actions } = useContext(ContractContext);

  const refreshContractDetails = useCallback(() => {
    if (id) actions?.getContract();
  }, [id]);

  const parties = useMemo(
    () => milestone?.partiesProfiles?.PROVIDER,
    [milestone]
  );
  const columns = [
    { type: "id", value: "" },
    { type: "name", value: `${t("lbl_party")}` },
    { type: "number", value: `${t("lbl_edit_profile_phone")}` },
    { type: "amount", value: `${t("lbl_value_sar")}` },
    { type: "fee", value: `${t("lbl_loyal_fee")}` },
    { type: "receive", value: `${t("lbl_loyal_receive")}` },
    { type: "status", value: `${t("lbl_condition")}` },
    { type: "action", value: "" },
  ];

  const transformPayToRows = useCallback(
    (parties): RowData[] => {
      parties?.forEach(function (party, index) {
        if (party?.phoneNumber === userInfo?.phoneNumber) {
          parties?.splice(index, 1);
          parties?.unshift(party);
        }
      });

      return parties?.map((item, index) => ({
        id: `${index + 1}.`,
        name: item?.profile?.fullName || t("lbl_not_registered_yet"),
        number: item?.phoneNumber,
        amount: partiesObj[`${item?.phoneNumber}`]?.amount,
        fee: partiesObj[`${item?.phoneNumber}`]?.fee,
        receive:
          (partiesObj[`${item?.phoneNumber}`]?.amount || 0) -
          (partiesObj[`${item?.phoneNumber}`]?.fee || 0),
        status: partiesObj[`${item?.phoneNumber}`]?.status,
        action: "",
      }));
    },
    [partiesObj, t, userInfo?.phoneNumber]
  );

  useEffect(() => {
    if (parties?.length) {
      setRows(transformPayToRows(parties));
    }
  }, [parties, parties?.length, transformPayToRows]);

  const renderCell = useCallback(
    (column: string, row: unknown) => {
      const rowData = row as RowData;
      switch (column) {
        case "id":
          return (
            <p className={`${styles["p3"]} ${styles["g600"]}`}>{rowData?.id}</p>
          );
        case "name":
          return (
            <p className={`${styles["p3"]} ${styles["g900"]}`}>
              {rowData?.name}
            </p>
          );
        case "number":
          return (
            <p
              style={{ direction: "ltr" }}
              className={`${styles["p3"]} ${styles["g900"]}`}>
              {rowData?.number}
            </p>
          );
        case "amount":
          return (
            <p className={`${styles["p3"]} ${styles["g900"]}`}>
              {rowData?.amount}
            </p>
          );
        case "fee":
          return (
            <p className={`${styles["p3"]} ${styles["g900"]}`}>
              {rowData?.fee}
            </p>
          );
        case "receive":
          return (
            <p className={`${styles["p3"]} ${styles["g900"]}`}>
              {rowData?.receive}
            </p>
          );
        case "status":
          return (
            <div>
              {milestone?.status !==
                ECONTRACT_STATUS.CLOSED_REFUND_CUSTOMER && (
                <>
                  {milestone?.partiesProfiles?.BROKER?.length ? (
                    milestone?.status !==
                      ECONTRACT_STATUS.WAITING_FOR_DELIVERY && (
                      <MilestonePartyStatus
                        party={row as Partial<UserProfile>}
                      />
                    )
                  ) : (
                    <MilestonePartyStatus party={row as Partial<UserProfile>} />
                  )}
                </>
              )}
            </div>
          );
        case "action":
          return (
            <div style={{ padding: "8px" }}>
              {canMakeContractActions(
                parties?.find((party) => party?.phoneNumber === rowData?.number)
              ) && (
                <ContractActions
                  handleMenuClose={refreshContractDetails}
                  contract={{
                    ...milestone,
                    receiverId: milestone?.receiverId || "-1",
                    senderId: `${
                      parties?.find(
                        (party) => party?.phoneNumber === rowData?.number
                      )?.id
                    }`,
                  }}
                  receiverType={ESenderRole.PROVIDER}
                  id={"receiving-table"}
                />
              )}
            </div>
          );

        default:
          return <p>{rowData[column]}</p>;
      }
    },
    [canMakeContractActions, milestone, parties, refreshContractDetails]
  );

  return <Table columns={columns} rows={rows} renderCell={renderCell} />;
};

export default React.memo(ReceivingTable);
